import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { useAccount } from "@/api/accounts";
import PremiumBadge from "@/components/premium-badge";
import { Avatar, Badge, HStack, Stack, Text } from "@/components/ui";
import VerificationBadge from "@/components/verification-badge";
import { useAuth } from "@/contexts/auth/useAuth";
import HeaderImage from "@/features/account/components/header-image";

import ProfileStats from "./profile/profile-stats";

interface UserPanelProps {
  accountId: string;
  action?: React.JSX.Element;
}

const UserPanel: React.FC<UserPanelProps> = ({ accountId, action }) => {
  const { account } = useAccount(accountId);
  const { currentAccount } = useAuth();

  const renderBadges = () => {
    if (!account || !currentAccount) return null;

    const isFollowedBy =
      currentAccount.id !== account.id && account.relationship?.followed_by;
    const isBlocking =
      currentAccount.id !== account.id && account.relationship?.blocking;
    const isMuting =
      currentAccount.id !== account.id && account.relationship?.muting;

    return (
      <div className="absolute right-2 bottom-2">
        <HStack alignItems="center" space={1} data-testid="badges">
          {isFollowedBy && (
            <Badge variant="overlay">
              <FormattedMessage
                id="account.follows_you"
                defaultMessage="Follows you"
              />
            </Badge>
          )}

          {isBlocking && (
            <Badge variant="overlay">
              <FormattedMessage id="account.blocked" defaultMessage="Blocked" />
            </Badge>
          )}

          {isMuting && (
            <Badge variant="overlay">
              <FormattedMessage id="account.muted" defaultMessage="Muted" />
            </Badge>
          )}
        </HStack>
      </div>
    );
  };

  if (!account) return null;

  return (
    <div className="relative">
      <Stack space={2}>
        <Stack>
          <div className="relative isolate -mx-4 -mt-4 h-24 overflow-hidden bg-gray-200 dark:bg-gray-800">
            <HeaderImage account={account} withLink={false} />

            {renderBadges()}
          </div>

          <HStack justifyContent="between" className="relative">
            <Link
              to={`/@${account.acct}`}
              title={account.acct}
              className="-mt-12 block"
            >
              <Avatar
                src={account.avatar_static}
                size={80}
                className="size-20 overflow-hidden bg-white ring-2 ring-white dark:bg-gray-900 dark:ring-gray-900"
              />
            </Link>

            {action && <div className="mt-2">{action}</div>}
          </HStack>
        </Stack>

        <Stack>
          <Link to={`/@${account.acct}`}>
            <HStack space={1} alignItems="center">
              <Text
                tag="div"
                size="lg"
                weight="bold"
                truncate
                dangerouslySetInnerHTML={{ __html: account.display_name_html }}
              />

              {account.verified && <VerificationBadge />}
              {account.premium && <PremiumBadge />}
            </HStack>
          </Link>

          <Text size="sm" theme="muted" truncate>
            @{account.acct}
          </Text>
        </Stack>

        <ProfileStats
          account={account}
          onClickHandler={(event) => {
            event.stopPropagation();
          }}
          textSize="sm"
        />
      </Stack>
    </div>
  );
};

export default UserPanel;
